import React from 'react';
import { Box, Flex, Text, Badge, Heading, useColorModeValue, IconButton } from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Droppable } from 'react-beautiful-dnd';
import TanLeadCard from './TanLeadCard';

/**
 * Kanban column component that renders a stage with its leads
 * @param {Object} props - Component props
 * @param {Object} props.stage - Stage object with id, name and color
 * @param {Array} props.leads - Array of leads for this stage
 * @param {boolean} props.dragDisabled - Whether drag functionality is disabled
 * @param {boolean} props.isCollapsed - Whether the column is collapsed
 * @param {Function} props.onToggleCollapse - Function to toggle collapse state
 * @returns {React.ReactElement} - Column component
 */
const TanColumn = ({ 
  stage, 
  leads = [], 
  dragDisabled = false,
  isCollapsed = false,
  onToggleCollapse = () => {}
}) => {
  console.log(`Column ${stage.name}: ${leads.length} leads`, { stageId: stage._id });
  const bgColor = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headerBgColor = useColorModeValue('white', 'gray.800');
  
  // Safe guard for null/undefined stage
  if (!stage) return null;
  
  return (
    <Box
      minW={isCollapsed ? '150px' : '300px'}
      maxW={isCollapsed ? '150px' : '300px'}
      mx={2}
      borderWidth="1px"
      borderRadius="md"
      bg={bgColor}
      borderColor={borderColor}
      height="100%"
      display="flex"
      flexDirection="column"
      data-testid={`column-${stage._id}`}
      transition="min-width 0.3s, max-width 0.3s"
    >
      {/* Column Header */}
      <Flex
        py={2}
        px={3}
        borderTopRadius="md"
        bg={headerBgColor}
        borderBottomWidth="1px"
        borderColor={borderColor}
        justifyContent="space-between"
        alignItems="center"
        position="sticky"
        top={0}
        zIndex={1}
      >
        <Heading
          as="h3"
          size="sm"
          fontWeight="semibold"
          display="flex"
          alignItems="center"
          color={stage.color || 'gray.700'}
        >
          {stage.name}
        </Heading>
        <Flex alignItems="center">
          {/* Lead count badge */}
          <Badge
            borderRadius="full"
            px={2}
            colorScheme={leads.length > 0 ? "blue" : "gray"}
            mr={2}
          >
            {leads.length}
          </Badge>
          {/* Toggle collapse button */}
          <IconButton
            aria-label={isCollapsed ? 'Expand column' : 'Collapse column'}
            icon={isCollapsed ? <ChevronRightIcon /> : <ChevronDownIcon />}
            size="sm"
            variant="ghost"
            onClick={onToggleCollapse}
          />
        </Flex>
      </Flex>

      {/* Droppable area for leads */}
      <Droppable droppableId={stage._id} isDropDisabled={dragDisabled}>
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            flex={1}
            overflowY="auto"
            p={2}
            display={isCollapsed ? 'none' : 'block'} /* Hide content when collapsed */
            bg={snapshot.isDraggingOver ? 'gray.100' : 'transparent'}
            transition="background-color 0.2s ease"
            sx={{
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                borderRadius: '3px',
              },
            }}
          >
            {leads.length === 0 ? (
              <Text
                fontSize="xs"
                color="gray.500"
                textAlign="center"
                py={4}
                fontStyle="italic"
              >
                No leads in this stage
              </Text>
            ) : (
              leads.map((lead, index) => {
                // Ensure each lead has a unique, stable ID for React and react-beautiful-dnd
                const uniqueId = lead.leadId || lead._id || `lead-${stage._id}-${index}`;
                return (
                  <TanLeadCard
                    key={uniqueId}
                    lead={{...lead, _uniqueId: uniqueId}} // Ensure lead always has a stable ID property
                    index={index}
                    dragDisabled={dragDisabled}
                  />
                );
              })
            )}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </Box>
  );
};

export default TanColumn;
